/**
 * خصومات الغياب والتأخرات — نفس منطق السجل التفصيلي (localTotals) و discounts-list / wages-list.
 * غياب = يوم عمل بلا أي بصمة. تأخرات = أي يوم فيه بصمة (مكتملة أو ناقصة).
 */

export function parseApiMoney(value) {
  if (value === undefined || value === null || value === "") {
    return null;
  }
  const n = Number(value);
  return Number.isNaN(n) ? null : n;
}

/** دقائق التأخر من HH:MM:SS (تشمل الثواني) */
export function getLateMinutesFromTime(lateTime) {
  if (!lateTime) return 0;
  const parts = lateTime.toString().split(".")[0].split(":");
  const h = Number(parts[0]) || 0;
  const m = Number(parts[1]) || 0;
  const s = Number(parts[2]) || 0;
  return Math.max(0, h * 60 + m + s / 60);
}

/** متوسط دقائق الدوام الشهرية = دقائق اليوم × 30 (مثلاً 420×30 = 12600) */
export function getMonthlyRequiredMinutes(requiredDailyMinutes) {
  const dayMin =
    Number(requiredDailyMinutes) > 0 ? Number(requiredDailyMinutes) : 7 * 60;
  return dayMin * 30;
}

/**
 * خصم التأخرات: (الراتب / دقائق_الشهر) × دقائق_التأخر
 * مثال: 262000 / 12600 × 828 = 17217
 */
export function calcLateDiscountAmount(
  salary,
  requiredDailyMinutes,
  lateValue,
  inputIsSeconds = true,
) {
  const monthly = getMonthlyRequiredMinutes(requiredDailyMinutes);
  const lateMinutes = inputIsSeconds
    ? Number(lateValue) / 60
    : Number(lateValue);
  if (!salary || lateMinutes <= 0 || monthly <= 0) return 0;
  return (Number(salary) / monthly) * lateMinutes;
}

/** عدد أيام الغياب (بلا بصمة) من الباك اند */
export function getAbsenceDays(row, fallbackRequiredCount = 30) {
  if (!row || row.fingerprint_type != "22") return 0;
  const fromApi = parseApiMoney(row.absenceDays);
  if (fromApi !== null && fromApi >= 0) {
    return Math.floor(fromApi);
  }
  const requiredDays =
    Number(row.requiredDays) > 0
      ? Number(row.requiredDays)
      : Number(fallbackRequiredCount) || 30;
  return Math.max(
    0,
    requiredDays -
      (Number(row.attendanceDays) || 0) -
      (Number(row.vacationDays) || 0),
  );
}

export function getDailySalaryFromRow(row) {
  if (Number(row?.dsalary) > 0) return Number(row.dsalary);
  const salary = Number(row?.salary) || 0;
  return salary > 0 ? salary / 30 : 0;
}

/**
 * خصم الغياب: عدد أيام الغياب × (الراتب / 30)
 * مثال: 224500 / 30 × 3 = 22450
 */
export function calcAbsenceDiscountAmount(salary, dsalary, absenceDays) {
  const days = Number(absenceDays) || 0;
  if (days <= 0) return 0;
  const daily =
    Number(dsalary) > 0
      ? Number(dsalary)
      : Number(salary) > 0
        ? Number(salary) / 30
        : 0;
  return days * daily;
}

/** مبلغ خصم الغياب — أيام بلا بصمة × أجر اليوم */
export function calcAbsenceDiscount(row, fallbackRequiredCount = 30) {
  if (!row || row.fingerprint_type != "22") return 0;

  const absenceDays = getAbsenceDays(row, fallbackRequiredCount);
  const amount = calcAbsenceDiscountAmount(
    row.salary,
    row.dsalary,
    absenceDays,
  );
  if (absenceDays > 0 && amount > 0) {
    return amount;
  }

  const fromApi = parseApiMoney(row.absenceDiscount);
  if (fromApi !== null) return fromApi;

  return 0;
}

/** مبلغ خصم التأخرات — (راتب/دقائق الشهر)×دقائق التأخر؛ أو من السجل التفصيلي بعد الإثراء */
export function calcLateDiscount(row, fallbackRequiredCount = 30) {
  if (!row || row.fingerprint_type != "22") return 0;

  const salary = Number(row.salary) || 0;
  const requiredDailyMinutes =
    Number(row.requiredDailyMinutes) > 0
      ? Number(row.requiredDailyMinutes)
      : 7 * 60;
  const lateMinutes = getLateMinutesFromTime(row.lateTime);

  if (row._fromDetailRecord && parseApiMoney(row.lateTimePrice) !== null) {
    return parseApiMoney(row.lateTimePrice);
  }

  if (salary > 0 && lateMinutes > 0) {
    return calcLateDiscountAmount(salary, requiredDailyMinutes, lateMinutes, false);
  }

  const absence = calcAbsenceDiscount(row, fallbackRequiredCount);
  const totalAttendance = parseApiMoney(row.attendanceDiscountTotal);
  if (totalAttendance !== null) {
    return Math.max(0, totalAttendance - absence);
  }
  const fromApi = parseApiMoney(row.lateTimePrice);
  if (fromApi !== null) return fromApi;

  return 0;
}

export function calcAttendanceDiscountTotal(row, options = {}) {
  const manual = Number(row?.manualDiscounts) || 0;
  return (
    calcAbsenceDiscount(row, options.fallbackRequiredCount) +
    calcLateDiscount(row) +
    manual
  );
}

export function applyDiscountRound(value, dround) {
  const n = Number(value) || 0;
  if (dround > 0) {
    return Math.round(n / dround) * dround;
  }
  return Math.round(n);
}
